import { DeviceMediaRequest, MediaRequests, useDialog } from "@app/shared";
import { FC, useState } from "react";
import JsonViewer from "./JsonViewer";

type MediaRequestJsonLinkProps = {
    request: DeviceMediaRequest;
};

/**
 * Turns the request id in the list into a link that shows the whole media request as JSON.
 *
 * The list columns only surface what fits a table — status, period, artifacts. Fields that matter to
 * someone integrating against the API (the delivered clip window, notices, expiry, the desired
 * encode settings) have no column and are otherwise only visible on a request raised by an event,
 * via the event clip payload. This gives the same view for a request raised any other way.
 *
 * Re-fetched on click rather than dumping the row already in memory: the row comes from the list
 * projection and can be a few seconds stale behind the SignalR updates, and re-fetching shows the
 * server's current answer for this one request — which is what someone comparing against their own
 * integration actually wants.
 */
export const MediaRequestJsonLink: FC<MediaRequestJsonLinkProps> = ({ request }) => {
    const jsonDialog = useDialog<DeviceMediaRequest>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const open = async () => {
        setIsLoading(true);
        setError(undefined);
        try {
            const fresh = await MediaRequests.getRequest(request.deviceId, request.id);
            // Fall back to the row we already have rather than failing outright: a stale view is more
            // useful here than none, and the dialog is read-only.
            jsonDialog.show("Media request JSON", "Close", (fresh ?? request) as DeviceMediaRequest, () => { });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <span
                role="button"
                tabIndex={0}
                title="View this request as JSON"
                className="cursor-pointer text-link-page hover:text-link-page-hover"
                onClick={() => { if (!isLoading) void open(); }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!isLoading) void open();
                    }
                }}
            >
                {isLoading ? "…" : `${request.id!.split("-")[0]}…`}
            </span>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

            {jsonDialog.renderDialog(
                (data) => (
                    <div className="max-h-[60vh] overflow-auto space-y-2">
                        <p className="text-xs text-semi-bright-text">
                            What <code>GET /api/mediarequest/&#123;deviceId&#125;/media-requests/&#123;id&#125;</code>
                            {" "}returns for this request. <code>videoDataStart</code> / <code>videoDataEnd</code> are
                            the window the delivered clip actually covers, which is not the period that was requested.
                        </p>
                        <JsonViewer data={data} />
                    </div>
                ),
                {
                    className: "w-[75%] overflow-hidden",
                    closeOnOutsideClick: true,
                    // Read-only: renderDialog adds a Cancel button whenever an onOk is supplied, which
                    // would give two buttons that both just close.
                    showCancelButton: false,
                }
            )}
        </>
    );
};
